// API interceptors for request/response handling

import type { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { logger } from '@/utils/logger';

// Extended config type with metadata
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  metadata?: {
    startTime?: number;
    [key: string]: unknown;
  };
  __retryCount?: number;
  __dedupKey?: string;
}

// Request timing middleware
export function addRequestTimingInterceptor(apiClient: AxiosInstance) {
  apiClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Add request timestamp
      const extConfig = config as ExtendedAxiosRequestConfig;
      extConfig.metadata = {
        ...extConfig.metadata,
        startTime: Date.now()
      };
      return config;
    }
  );

  apiClient.interceptors.response.use(
    (response: AxiosResponse) => {
      const extConfig = response.config as ExtendedAxiosRequestConfig;
      const duration = Date.now() - (extConfig.metadata?.startTime ?? Date.now());
      
      // Log slow requests
      if (duration > 3000) {
        logger.warn('[API] Slow request detected', {
          module: 'api',
          action: 'slow-request',
          metadata: {
            url: response.config.url,
            method: response.config.method,
            duration: `${duration}ms`,
          },
        });
      }
      
      return response;
    }
  );
}

// Request retry interceptor
export function addRetryInterceptor(apiClient: AxiosInstance, maxRetries = 3) {
  apiClient.interceptors.response.use(
    undefined,
    async (error: { config?: ExtendedAxiosRequestConfig; response?: { status: number } }) => {
      const config = error.config;

      // Skip retry for certain conditions
      if (!config ||
          (config.__retryCount ?? 0) >= maxRetries ||
          (error.response?.status ?? 0) < 500 ||
          config.method !== 'get') {
        return Promise.reject(error);
      }

      // Increment retry count
      config.__retryCount = (config.__retryCount ?? 0) + 1;
      
      // Exponential backoff
      const delay = Math.min(1000 * Math.pow(2, config.__retryCount - 1), 10000);
      
      logger.info(`[API] Retrying request (${config.__retryCount}/${maxRetries})`, {
        module: 'api',
        action: 'retry',
        metadata: {
          url: config.url,
          delay: `${delay}ms`,
        },
      });
      
      await new Promise(resolve => setTimeout(resolve, delay));
      
      return apiClient(config);
    }
  );
}

// Request deduplication interceptor
const pendingRequests = new Map<string, Promise<AxiosResponse>>();

export function addDeduplicationInterceptor(apiClient: AxiosInstance) {
  apiClient.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      // Only deduplicate GET requests
      if (config.method !== 'get') {
        return config;
      }
      
      // Create request key
      const key = `${config.method}:${config.url}:${JSON.stringify(config.params)}`;
      
      // Check if request is already pending
      if (pendingRequests.has(key)) {
        logger.debug('[API] Deduplicating request', {
          module: 'api',
          action: 'deduplicate',
          metadata: { key },
        });
        const response = await pendingRequests.get(key);
        
        // Return a config that will be rejected to prevent actual request
        return {
          ...config,
          adapter: () => Promise.resolve(response)
        } as InternalAxiosRequestConfig;
      }
      
      // Store promise resolver
      (config as ExtendedAxiosRequestConfig).__dedupKey = key;

      return config;
    }
  );

  apiClient.interceptors.response.use(
    (response: AxiosResponse) => {
      const key = (response.config as ExtendedAxiosRequestConfig).__dedupKey;
      if (key && pendingRequests.has(key)) {
        pendingRequests.delete(key);
      }
      return response;
    },
    (error: { config?: ExtendedAxiosRequestConfig }) => {
      const key = error.config?.__dedupKey;
      if (key && pendingRequests.has(key)) {
        pendingRequests.delete(key);
      }
      return Promise.reject(error);
    }
  );
}

// Add all interceptors to an axios instance
export function setupInterceptors(apiClient: AxiosInstance) {
  addRequestTimingInterceptor(apiClient);
  addRetryInterceptor(apiClient);
  addDeduplicationInterceptor(apiClient);
}
